import {announce} from '@github-ui/aria-live'
import {useReportErrorContext} from '@github-ui/react-core/future/report-error-context'
import {keepPreviousData, useInfiniteQuery, type InfiniteData, type QueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useMemo, useRef} from 'react'
import type {SidebarSelectedWorkflow} from '../routes/workflows-layout-route'
import type {Workflow} from '../views/models'

/**
 * Route ID the workflow sidebar endpoint namespaces its React payload under. This endpoint is not
 * modelled as a client route (it is fetched imperatively, never navigated to), so the ID is declared
 * here to match `Actions::WorkflowsController#sidebar` rather than derived from a route object.
 */
const WORKFLOW_SIDEBAR_ROUTE_ID = 'workflowsSidebarRoute'

export type WorkflowSidebarSortMode =
  | 'default'
  | 'name-ascending'
  | 'name-descending'
  | 'last-run-descending'
  | 'last-run-ascending'

const SORT_PARAMS: Record<WorkflowSidebarSortMode, string> = {
  default: 'default',
  'name-ascending': 'name-asc',
  'name-descending': 'name-desc',
  'last-run-descending': 'last-run-desc',
  'last-run-ascending': 'last-run-asc',
}

interface UseWorkflowPaginationArgs {
  initialWorkflows: Workflow[]
  totalCount: number
  owner: string
  repo: string
  /** Debounced search term. An empty string requests the unfiltered list. */
  query: string
  sortMode: WorkflowSidebarSortMode
  /** Echoed by the layout payload; later default-order pages must be requested with it. */
  selectedWorkflow?: SidebarSelectedWorkflow | null
}

interface WorkflowPaginationState {
  workflows: Workflow[]
  /** Number of workflows matching the current query, which may exceed the number loaded. */
  totalCount: number
  hasMore: boolean
  loading: boolean
  loadError: boolean
  /** True while a new query/sort combination is being fetched, as opposed to an additional page. */
  searching: boolean
  searchError: boolean
  retrySearch: () => void
  loadMore: (announcementElement: HTMLElement) => Promise<LoadMoreResult>
}

interface LoadMoreResult {
  loadedFinalPage: boolean
  hadError: boolean
  skipped: boolean
}

interface WorkflowPage {
  workflows: Workflow[]
  totalCount: number
}

/**
 * Prefix shared by every query/sort variant of the sidebar list, for invalidating them together.
 */
export function workflowSidebarQueryKeyPrefix(owner: string, repo: string) {
  return ['actions-workflows', 'workflow-sidebar-pagination', owner, repo] as const
}

export function workflowPaginationQueryKey(
  owner: string,
  repo: string,
  {
    query,
    sortMode,
    selectedWorkflow,
  }: {query: string; sortMode: WorkflowSidebarSortMode; selectedWorkflow?: SidebarSelectedWorkflow | null},
) {
  const selection = selectedWorkflow ? `${selectedWorkflow.lab ? 'lab:' : ''}${selectedWorkflow.fileName}` : null
  return [...workflowSidebarQueryKeyPrefix(owner, repo), query, sortMode, selection] as const
}

/**
 * Applies an optimistic state change to every cached query/sort variant of the sidebar list, so a
 * workflow disabled while a search is active still shows as disabled after the search is cleared.
 */
export function updateWorkflowStateInPaginationCache({
  queryClient,
  owner,
  repo,
  workflowId,
  state,
}: {
  queryClient: QueryClient
  owner: string
  repo: string
  workflowId: bigint
  state: Workflow['state']
}) {
  queryClient.setQueriesData<InfiniteData<WorkflowPage, number>>(
    {queryKey: workflowSidebarQueryKeyPrefix(owner, repo)},
    oldData => {
      if (!oldData) return oldData

      let updated = false
      const pages = oldData.pages.map(page => {
        let pageUpdated = false
        const workflows = page.workflows.map(workflow => {
          if (BigInt(workflow.id) !== workflowId || workflow.state === state) return workflow

          updated = true
          pageUpdated = true
          return {...workflow, state}
        })

        return pageUpdated ? {...page, workflows} : page
      })

      return updated ? {...oldData, pages} : oldData
    },
  )
}

export function useWorkflowPagination({
  initialWorkflows,
  totalCount,
  owner,
  repo,
  query,
  sortMode,
  selectedWorkflow,
}: UseWorkflowPaginationArgs): WorkflowPaginationState {
  const reportError = useReportErrorContext()
  const loadMorePendingRef = useRef(false)
  // The layout payload already contains the first page of the unfiltered, default-sorted list, so
  // that variant is seeded rather than refetched. Every other variant has to come from the server.
  const isInitialVariant = query === '' && sortMode === 'default'
  // The server only promotes the selected workflow in the default, unfiltered order.
  const pageSelection = isInitialVariant ? selectedWorkflow : null
  // Paginated sidebar data is ephemeral and this endpoint does not yet provide a schema.
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema
  const {data, error, fetchNextPage, isFetching, isFetchingNextPage, isFetchNextPageError, refetch} = useInfiniteQuery({
    queryKey: workflowPaginationQueryKey(owner, repo, {query, sortMode, selectedWorkflow: pageSelection}),
    queryFn: async ({pageParam, signal}) => {
      const params = new URLSearchParams({page: String(pageParam), sort: SORT_PARAMS[sortMode]})
      if (query) params.set('q', query)
      if (pageSelection) {
        params.set('workflow_file_name', pageSelection.fileName)
        if (pageSelection.lab) params.set('lab', 'true')
      }
      const response = await reactFetchJSON(`/${owner}/${repo}/actions/_workflows?${params.toString()}`, {signal})
      if (!response.ok) throw new Error(`Failed to load workflows: ${response.status}`)
      const result = (await response.json()) as {payload: Record<string, WorkflowPage>}
      const pageData = result.payload[WORKFLOW_SIDEBAR_ROUTE_ID]
      if (!pageData) throw new Error('Workflow sidebar response did not include route data')
      return {workflows: pageData.workflows, totalCount: pageData.totalCount} satisfies WorkflowPage
    },
    initialPageParam: 1,
    initialData: isInitialVariant
      ? {
          pages: [{workflows: initialWorkflows, totalCount}],
          pageParams: [1],
        }
      : undefined,
    // Keeps the previously matching workflows on screen while the next query/sort is in flight,
    // rather than collapsing the sidebar to an empty list on every keystroke.
    placeholderData: keepPreviousData,
    staleTime: Infinity,
    getNextPageParam: (lastPage, pages, lastPageParam) => {
      const loadedCount = pages.reduce((count, page) => count + page.workflows.length, 0)
      return loadedCount < lastPage.totalCount ? lastPageParam + 1 : undefined
    },
    retry: false,
  })

  const workflows = useMemo(() => data?.pages.flatMap(page => page.workflows) ?? [], [data?.pages])
  const currentPage = data?.pages[data.pages.length - 1]
  const currentTotalCount = currentPage ? currentPage.totalCount : 0
  const hasMore = workflows.length < currentTotalCount
  // `isFetching` also covers "show more", which has its own loading affordance.
  const searching = isFetching && !isFetchingNextPage
  const searchError = Boolean(error) && !isFetchNextPageError

  const retrySearch = useCallback(() => {
    void refetch()
  }, [refetch])

  const loadMore = useCallback(
    async (announcementElement: HTMLElement) => {
      if (!hasMore) return {loadedFinalPage: false, hadError: false, skipped: true}
      if (loadMorePendingRef.current) return {loadedFinalPage: false, hadError: false, skipped: true}

      loadMorePendingRef.current = true
      try {
        announce('Loading workflows…', {element: announcementElement})
        const result = await fetchNextPage({cancelRefetch: false})
        if (result.isError) {
          reportError(result.error)
          announce('Could not load workflows. Try again.', {element: announcementElement})
          loadMorePendingRef.current = false
          return {loadedFinalPage: false, hadError: true, skipped: false}
        }
        const loadedCount = result.data?.pages.reduce((count, page) => count + page.workflows.length, 0) ?? 0
        const refreshedPages = result.data?.pages
        const refreshedPage = refreshedPages?.[refreshedPages.length - 1]
        const refreshedTotalCount = refreshedPage ? refreshedPage.totalCount : currentTotalCount
        loadMorePendingRef.current = false
        return {loadedFinalPage: loadedCount >= refreshedTotalCount, hadError: false, skipped: false}
      } catch (fetchError) {
        loadMorePendingRef.current = false
        throw fetchError
      }
    },
    [currentTotalCount, fetchNextPage, hasMore, reportError],
  )

  return useMemo(
    () => ({
      workflows,
      totalCount: currentTotalCount,
      hasMore,
      loading: isFetchingNextPage,
      loadError: isFetchNextPageError,
      searching,
      searchError,
      retrySearch,
      loadMore,
    }),
    [
      currentTotalCount,
      hasMore,
      isFetchNextPageError,
      isFetchingNextPage,
      loadMore,
      retrySearch,
      searchError,
      searching,
      workflows,
    ],
  )
}
